import type { ILogTransporter, LogMessage } from 'fixparser-common';

/**
 * Logger output format options.
 *
 * - 'console': Output log in plain text format
 * - 'json': Output log in JSON format
 * - 'jsonrpc': Output log in JSON-RPC 2.0 format
 *
 * @public
 */
export type ConsoleFormat = 'console' | 'json' | 'jsonrpc';

/**
 * A LogTransporter implementation for logging to the console.
 * It supports text (console), JSON, and JSON-RPC 2.0 formats.
 */
export class ConsoleLogTransport implements ILogTransporter {
    private format: ConsoleFormat;
    private useStderr: boolean;

    constructor({ format = 'json', useStderr = false }: { format: ConsoleFormat; useStderr?: boolean }) {
        this.format = format;
        this.useStderr = useStderr;
    }

    /**
     * Configures the format for console logging (either 'console' for text, 'json', or 'jsonrpc').
     */
    configure(config: { format: 'console' | 'json' | 'jsonrpc'; useStderr?: boolean }): void {
        this.format = config.format || 'json';
        if (config.useStderr !== undefined) {
            this.useStderr = config.useStderr;
        }
    }

    /**
     * Sends the log message to the console in the configured format.
     */
    async send(log: LogMessage): Promise<void> {
        const logMethod = this.useStderr ? console.error : console.log;

        if (this.format === 'json') {
            logMethod(JSON.stringify(log));
        } else if (this.format === 'jsonrpc') {
            const { message, ...rest } = log;
            const jsonrpcMessage = {
                jsonrpc: '2.0',
                method: log.level,
                params: {
                    message,
                    ...rest,
                },
                id: log.id || Date.now(),
            };
            logMethod(JSON.stringify(jsonrpcMessage));
        } else {
            const { name, id, message, level, ...additionalProperties } = log;
            const kv = Object.entries(additionalProperties).map(([key, value]) => `${key}: ${value}`);
            let logMessage = '';
            if (name) {
                logMessage += `${name} `;
            }
            logMessage += `${id}: ${message}`;
            void kv;
            void level;
            logMethod(logMessage, kv.join(', '));
        }
    }

    /**
     * Flushes the log buffer (if any buffering mechanism exists).
     */
    async flush(): Promise<void> {
        // No flushing needed for console transport
    }

    /**
     * Closes the transport (not needed for console, but keeping the method for consistency).
     */
    async close(): Promise<void> {
        // No close logic needed for console transport
    }

    /**
     * Returns the status of the transport (always "connected" for console).
     */
    status(): string {
        return 'connected';
    }
}
